import { toast } from "sonner";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, AlertTriangle, FileText, Mic, Zap, ArrowRight, Copy, Check } from "lucide-react";
import ScoreTierBadge from "@/components/wishire/ScoreTierBadge";
import { useAuth } from "@/contexts/AuthContext";
import {
  getScoreTier,
  getScoreHistory,
  verifyIdentity,
  getVerificationStatus,
} from "@/lib/api";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

export default function ScoreInsightsPage() {
  const { user } = useAuth();
  const [tierData, setTierData] = useState(null);
  const [history, setHistory] = useState([]);
  const [verif, setVerif] = useState({ linkedin: false, aadhaar: false });
  const [loading, setLoading] = useState(true);

  // LinkedIn verification form — prefill with the URL the user already gave at onboarding.
  const [linkedinUrl, setLinkedinUrl] = useState(user?.linkedin_url || "");
  const [aadhaarLast4, setAadhaarLast4] = useState("");
  const [submitting, setSubmitting] = useState(null);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: tier }, { data: hist }, { data: vs }] = await Promise.allSettled([
          getScoreTier(),
          getScoreHistory(),
          getVerificationStatus(),
        ]).then((res) =>
          res.map((r) => (r.status === "fulfilled" ? r.value : { data: null }))
        );
        if (tier) setTierData(tier);
        // Backend returns { events: [...] } — pull the array out (object-vs-array guard)
        if (hist) setHistory(hist?.events || hist?.history || (Array.isArray(hist) ? hist : []));
        // Backend returns { current_level, method, ... } — a single verification model
        if (vs) {
          const verified = vs.current_level && vs.current_level !== "unverified";
          setVerif({
            linkedin: !!(verified && vs.method === "linkedin"),
            aadhaar: !!(verified && vs.method === "aadhaar_partial"),
          });
        }
      } catch (e) {
        // Use fallback state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // If auth resolves after mount, prefill the LinkedIn field from the stored profile URL.
  useEffect(() => {
    if (user?.linkedin_url) setLinkedinUrl((cur) => cur || user.linkedin_url);
  }, [user?.linkedin_url]);

  const score = Math.round(tierData?.adjusted_score ?? tierData?.raw_score ?? tierData?.score ?? 0);
  const decayed = tierData?.decay_applied ?? tierData?.decayed ?? false;
  const decayAmount = Math.round((tierData?.raw_score ?? 0) - (tierData?.adjusted_score ?? 0));

  const handleVerify = async (type) => {
    setSubmitting(type);
    try {
      // Backend expects { method: "linkedin" | "aadhaar_partial", value }
      const payload =
        type === "linkedin"
          ? { method: "linkedin", value: linkedinUrl }
          : { method: "aadhaar_partial", value: aadhaarLast4 };
      await verifyIdentity(payload);
      setVerif((v) => ({ ...v, [type]: true }));
      setSubmitted(type);
    } catch (e) {
      toast.error("Verification failed. Please try again.");
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="container-x py-16 flex items-center justify-center text-stone-500">
        Loading score insights…
      </div>
    );
  }

  return (
    <div className="container-x py-10 md:py-14 space-y-10">
      {/* ── Hero ───────────────────────────────────────────── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="rounded-[2rem] bg-stone-900 text-stone-50 p-10 md:p-14 relative overflow-hidden wsh-grain"
      >
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 400px at 80% 10%, rgba(96,165,250,0.3), transparent 60%), radial-gradient(400px 300px at 0% 100%, rgba(217,119,6,0.25), transparent 60%)",
          }}
        />
        <div className="relative flex flex-col md:flex-row md:items-center gap-10">
          <div className="flex-1">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>
              Your Wishire Score
            </span>
            <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight">
              Score Insights
            </h1>
            {decayed && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-900/60 text-red-300 text-sm">
                <AlertTriangle size={14} />
                Score decayed by {decayAmount} pts — stay active to maintain your tier
              </div>
            )}
            <p className="mt-4 text-stone-300 max-w-md leading-relaxed">
              Your tier reflects your combined readiness score across resume, interviews,
              LinkedIn, and verified identity signals.
            </p>
          </div>
          <div className="flex justify-center md:justify-end">
            <ScoreTierBadge score={score} size="lg" />
          </div>
        </div>
      </motion.div>

      {/* ── Score History Chart ─────────────────────────────── */}
      {history.length > 1 && <ScoreHistoryBars history={history} />}

      {/* ── Identity Verification ───────────────────────────── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="rounded-3xl border border-stone-200 bg-white p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-2xl bg-blue-50 text-blue-600">
            <ShieldCheck size={20} />
          </div>
          <span className="overline">Identity Verification</span>
        </div>
        <h2 className="font-display text-2xl mb-2">
          Verify your identity to unlock +3 score bonus
        </h2>
        <p className="text-stone-500 mb-8 max-w-lg leading-relaxed">
          Verified profiles build recruiter trust and earn permanent score points that
          don&apos;t decay.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* LinkedIn */}
          <VerifCard
            title="LinkedIn Verification"
            description="Confirm your LinkedIn profile is yours."
            done={verif.linkedin}
            bonus="+2 pts"
            color="blue"
          >
            {verif.linkedin ? (
              <VerifiedBadge />
            ) : (
              <>
                <div className="mt-4 flex gap-2">
                  <input
                    type="url"
                    placeholder="https://linkedin.com/in/your-handle"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    className="flex-1 px-4 py-2.5 rounded-full bg-stone-100 border border-stone-300 text-sm focus:outline-none focus:border-stone-900"
                  />
                  <button
                    onClick={() => handleVerify("linkedin")}
                    disabled={!linkedinUrl || submitting === "linkedin"}
                    className="btn-primary text-sm disabled:opacity-50"
                  >
                    {submitting === "linkedin" ? "…" : "Verify"}
                  </button>
                </div>
                {user?.linkedin_url && linkedinUrl === user.linkedin_url && (
                  <p className="mt-2 text-xs text-stone-500">
                    Using the LinkedIn from your profile — just hit Verify, or edit it above.
                  </p>
                )}
              </>
            )}
          </VerifCard>

          {/* Aadhaar — retail candidates only; a campus student is already verified by their college */}
          {!user?.college_id && <VerifCard
            title="Aadhaar Partial Verify"
            description="Last 4 digits only — we never store your full Aadhaar number."
            done={verif.aadhaar}
            bonus="+1 pt"
            color="green"
          >
            {verif.aadhaar ? (
              <VerifiedBadge />
            ) : (
              <>
                <div className="mt-4 flex gap-2">
                  <input
                    type="text"
                    maxLength={4}
                    placeholder="Last 4 digits"
                    value={aadhaarLast4}
                    onChange={(e) => setAadhaarLast4(e.target.value.replace(/\D/g, ""))}
                    className="w-32 px-4 py-2.5 rounded-full bg-stone-100 border border-stone-300 text-sm font-mono tracking-widest focus:outline-none focus:border-stone-900"
                  />
                  <button
                    onClick={() => handleVerify("aadhaar")}
                    disabled={aadhaarLast4.length !== 4 || submitting === "aadhaar"}
                    className="btn-primary text-sm disabled:opacity-50"
                  >
                    {submitting === "aadhaar" ? "…" : "Submit"}
                  </button>
                </div>
                <p className="mt-2 text-xs text-stone-400 leading-snug max-w-xs">
                  We only store the last 4 digits as a hash for verification purposes. Your
                  full Aadhaar number is never transmitted or stored.
                </p>
              </>
            )}
          </VerifCard>}
        </div>
      </motion.div>

      {/* ── How to Improve ─────────────────────────────────── */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <span className="overline">Improve your score</span>
        <h2 className="font-display text-2xl mt-2 mb-6">Three actions that move the needle.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActionCard
            icon={FileText}
            title="Score your resume"
            description="Résumé and skills match are a quarter of your score."
            to="/app/resume"
            cta="Upload resume"
            color="amber"
          />
          <ActionCard
            icon={Mic}
            title="Run a mock interview"
            description="The interview is the heaviest part of your score. Only full attempts (90 s, 3+ answers) count."
            to="/app/interview"
            cta="Start interview"
            color="terracotta"
          />
          <ActionCard
            icon={Zap}
            title="Close skill gaps"
            description="Targeted learning resources for your target role."
            to="/app/skills"
            cta="View skill gaps"
            color="olive"
          />
        </div>
      </motion.div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function ScoreHistoryBars({ history }) {
  const pts = history.slice(-10);
  const max = 100;

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className="rounded-3xl border border-stone-200 bg-white p-8 md:p-10"
    >
      <span className="overline">Score history</span>
      <h2 className="font-display text-2xl mt-2 mb-6">Your last {pts.length} score events</h2>

      <div className="flex items-end gap-2 h-40">
        {pts.map((p, i) => {
          const pct = Math.max(4, Math.round((p.score / max) * 100));
          const color =
            p.score >= 80 ? "#15803D" : p.score >= 60 ? "#D97706" : "#C2410C";
          return (
            <div key={i} className="flex-1 h-full flex flex-col items-center justify-end gap-1.5">
              <span className="text-xs font-mono text-stone-500">{p.score}</span>
              <motion.div
                className="w-full rounded-t-lg"
                style={{ background: color }}
                initial={{ height: 0 }}
                whileInView={{ height: `${pct}%` }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.6, ease: "easeOut" }}
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-between text-xs text-stone-400 font-mono mt-2">
        <span>
          {pts[0]?.created_at
            ? new Date(pts[0].created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
            : "earlier"}
        </span>
        <span>
          {pts[pts.length - 1]?.created_at
            ? new Date(pts[pts.length - 1].created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
            : "today"}
        </span>
      </div>
    </motion.div>
  );
}

function VerifCard({ title, description, done, bonus, color, children }) {
  const ring = color === "blue" ? "border-blue-200 bg-blue-50/40" : "border-emerald-200 bg-emerald-50/40";
  const pill = color === "blue" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700";
  return (
    <div className={`rounded-2xl border p-6 ${done ? ring : "border-stone-200 bg-stone-50"}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-lg">{title}</h3>
        <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${pill}`}>{bonus}</span>
      </div>
      <p className="text-sm text-stone-500 leading-snug">{description}</p>
      {children}
    </div>
  );
}

function VerifiedBadge() {
  return (
    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
      <Check size={14} /> Verified
    </div>
  );
}

function ActionCard({ icon: Icon, title, description, to, cta, color }) {
  const accent =
    color === "amber"
      ? "bg-amber-100 text-amber-700"
      : color === "terracotta"
      ? "bg-orange-100 text-[#C2410C]"
      : "bg-emerald-100 text-emerald-700";

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6 hover:shadow-md transition-shadow">
      <div className={`inline-flex p-3 rounded-2xl mb-4 ${accent}`}>
        <Icon size={20} />
      </div>
      <h3 className="font-display text-lg mb-1">{title}</h3>
      <p className="text-sm text-stone-500 leading-snug mb-4">{description}</p>
      <Link
        to={to}
        className="inline-flex items-center gap-1 text-sm font-medium text-[#C2410C] hover:text-stone-900 transition-colors"
      >
        {cta} <ArrowRight size={14} />
      </Link>
    </div>
  );
}
